import { stringNormalize } from '../../Shared/quill-marking-logic/src/libs/stringNormalize'
import { stripHtml, } from 'string-strip-html'

import { splitPromptForFillInBlank } from '../../Shared/libs/splitPromptForFillInBlank';

/**
 * Find the best fuzzy match for a pattern in the text.
 * Returns the index and length of the match, or -1 if no reasonable match found.
 */
function fuzzyIndexOf(text: string, pattern: string, startFrom: number = 0): { index: number; length: number } {
  if (!text || !pattern) return { index: -1, length: 0 };

  const searchText = text.substring(startFrom).toLowerCase();
  const searchPattern = pattern.toLowerCase();

  // First try exact match
  const exactIndex = searchText.indexOf(searchPattern);
  if (exactIndex >= 0) {
    return { index: startFrom + exactIndex, length: pattern.length };
  }

  // Try matching individual words from the pattern
  const patternWords = searchPattern.split(/\s+/).filter(w => w.length > 0);
  if (patternWords.length === 0) return { index: -1, length: 0 };

  // Find the first word of the pattern
  const firstWordIndex = searchText.indexOf(patternWords[0]);
  if (firstWordIndex === -1) return { index: -1, length: 0 };

  // If single word pattern, return it
  if (patternWords.length === 1) {
    return { index: startFrom + firstWordIndex, length: patternWords[0].length };
  }

  // For multi-word patterns, find the last word and calculate the span
  const lastWord = patternWords[patternWords.length - 1];
  const searchAfterFirst = searchText.substring(firstWordIndex + patternWords[0].length);
  const lastWordIndex = searchAfterFirst.indexOf(lastWord);

  if (lastWordIndex >= 0) {
    const matchStart = startFrom + firstWordIndex;
    const matchEnd = startFrom + firstWordIndex + patternWords[0].length + lastWordIndex + lastWord.length;
    return { index: matchStart, length: matchEnd - matchStart };
  }

  return { index: -1, length: 0 };
}

/**
 * Check if text starts with pattern, using fuzzy matching if exact match fails.
 * Returns the length of the matched portion, or 0 if no match.
 *
 * Even if words differ (e.g., prompt changed from "This was" to "This is"),
 * we assume the structure is the same and consume the same number of words
 * from the text as the pattern has.
 */
function fuzzyStartsWith(text: string, pattern: string): number {
  if (!text || !pattern) return 0;

  const normalizedText = text.toLowerCase();
  const normalizedPattern = pattern.toLowerCase();

  // Exact match
  if (normalizedText.startsWith(normalizedPattern)) {
    return pattern.length;
  }

  // Try word-by-word matching from the start
  const textWords = text.split(/\s+/).filter(w => w.length > 0);
  const patternWords = pattern.split(/\s+/).filter(w => w.length > 0);

  if (patternWords.length === 0) return 0;
  if (textWords.length === 0) return 0;

  // Consume the same number of words from text as pattern has,
  // even if the words have changed.
  const wordsToConsume = Math.min(patternWords.length, textWords.length);

  let matchedLength = 0;
  for (let i = 0; i < wordsToConsume; i++) {
    const wordPos = text.indexOf(textWords[i], matchedLength);
    matchedLength = wordPos + textWords[i].length;
  }

  return matchedLength;
}

/**
 * Check if text ends with pattern, using fuzzy matching if exact match fails.
 * Returns the length of the matched portion from the end, or 0 if no match.
 *
 * Even if the ending words differ (e.g., prompt changed from "tomorrow" to "today"),
 * we assume the structure is the same and consume the same number of words from the end.
 */
function fuzzyEndsWith(text: string, pattern: string): number {
  if (!text || !pattern) return 0;

  const normalizedText = text.toLowerCase();
  const normalizedPattern = pattern.toLowerCase();

  // Exact match
  if (normalizedText.endsWith(normalizedPattern)) {
    return pattern.length;
  }

  // Try word-by-word matching from the end
  const textWords = text.split(/\s+/).filter(w => w.length > 0);
  const patternWords = pattern.split(/\s+/).filter(w => w.length > 0);

  if (patternWords.length === 0) return 0;
  if (textWords.length === 0) return 0;

  // Consume the same number of words from the end of text as pattern has.
  // This handles cases where the prompt ending was edited after the student responded.
  const wordsToConsume = Math.min(patternWords.length, textWords.length);

  const matchedTextWords = textWords.slice(textWords.length - wordsToConsume);
  const matchStartIndex = text.lastIndexOf(matchedTextWords[0]);
  return text.length - matchStartIndex;
}

/**
 * Extract individual blank values from a response text based on the prompt structure.
 * The response text is the full sentence with blanks filled in (e.g., "He is a good friend")
 * Uses fuzzy matching to handle cases where the prompt has changed slightly.
 *
 * @param responseText - The student's full response (e.g., "He is a good friend")
 * @param prompt - The question prompt with blanks marked as ___ (e.g., "He is ___ ___ friend.")
 * @returns Array of values for each blank position (e.g., ["a", "good"])
 *
 * @example
 * extractBlankValues("He is a good friend", "He is ___ ___ friend.")
 * // Returns: ["a", "good"]
 */
export function extractBlankValues(responseText: string, prompt: string): string[] {
  // Handle empty or invalid inputs
  if (!responseText || !prompt) {
    return [];
  }

  // Split prompt by ___ to get the parts around the blanks
  // splitPromptForFillInBlank handles HTML properly, so call it first
  const promptParts = splitPromptForFillInBlank(stripHtml(prompt.replace('&#x27;', '\u0027')).result);
  const numberOfBlanks = promptParts.length - 1;

  if (numberOfBlanks === 0) {
    return [];
  }

  // Trim and normalize whitespace
  let remaining = responseText.trim().replace(/\s+/g, ' ');

  const blankValues: string[] = [];

  // Extract blank values by removing prompt parts sequentially
  for (let i = 0; i < promptParts.length; i++) {
    const promptPart = stringNormalize(promptParts[i]).trim().replace(/\s+/g, ' ');

    if (i === 0) {
      // Remove the beginning prompt part using fuzzy matching
      if (promptPart) {
        const matchLength = fuzzyStartsWith(remaining, promptPart);
        if (matchLength > 0) {
          remaining = remaining.substring(matchLength).trim();
        }
      }
    } else if (i === promptParts.length - 1) {
      // Last part - everything remaining before this is the last blank value
      if (promptPart) {
        const matchLength = fuzzyEndsWith(remaining, promptPart);
        if (matchLength > 0) {
          const blankValue = remaining.substring(0, remaining.length - matchLength).trim();
          blankValues.push(blankValue || '');
        } else {
          // If ending doesn't match, just take the remaining as the blank value
          if (remaining) {
            blankValues.push(remaining);
          }
        }
      } else {
        // No ending prompt part, take remaining as blank value
        if (remaining) {
          blankValues.push(remaining);
        }
      }
    } else {
      // Middle parts - find the next prompt part and extract what's before it
      if (promptPart) {
        const { index: nextIndex, length: matchLength } = fuzzyIndexOf(remaining, promptPart);
        if (nextIndex >= 0) {
          const blankValue = remaining.substring(0, nextIndex).trim();
          blankValues.push(blankValue || '');
          remaining = remaining.substring(nextIndex + matchLength).trim();
        } else {
          // Can't find the next part - assume structure is the same.
          // Take 1 word as the blank value, then skip N words for the pattern.
          const words = remaining.split(/\s+/).filter(w => w.length > 0);
          const patternWordCount = promptPart.split(/\s+/).filter(w => w.length > 0).length;

          if (words.length > 0) {
            blankValues.push(words[0]);
            // Skip 1 (for the blank) + patternWordCount (for the middle part)
            const wordsToSkip = 1 + patternWordCount;
            remaining = words.slice(wordsToSkip).join(' ');
          }
        }
      } else {
        // Empty prompt part, just split by space
        const words = remaining.split(/\s+/).filter(w => w.length > 0);
        if (words.length > 0) {
          blankValues.push(words[0]);
          remaining = words.slice(1).join(' ');
        }
      }
    }
  }

  return blankValues;
}
